<template>
  <div v-show="showWidget" class="typesense-search-widget" :style="{ top }">
    <div class="p-3">
      <input
        type="text"
        :placeholder="searchPlaceholder"
        class="typesense-search-form"
        ref="searchField"
        v-model="searchFilter"
      />
    </div>
    <div class="px-3 pb-3" v-if="!mergeTypesenseSearchResult">
      <div class="underline" v-if="mode === 'city'">
        {{ $t("listing.propertyInMind") }}
        <b
          class="text-main ml-2 cursor-pointer"
          @click="switchMode('property')"
          >{{ $t("listing.searchByName") }}</b
        >
      </div>
      <div class="underline" v-else>
        {{ $t("listing.visitingACity") }}
        <b class="text-main ml-2 cursor-pointer" @click="switchMode('city')">{{
          $t("listing.searchByCityName")
        }}</b>
      </div>
    </div>
    <hr class="m-0 p-0" />
    <div class="mt-2 option-list">
      <div
        v-for="result in filteredResults"
        :key="result.id"
        class="py-2 px-3 option-item"
        @click="selectDestination(result)"
      >
        <img
          class="mr-2"
          src="../../assets/icons/property.svg"
          v-if="result.type === 'property' || (!mergeTypesenseSearchResult && mode === 'property')"
          alt="Property"
        />
        <img
          class="mr-2"
          src="../../assets/icons/location.svg"
          v-else
          alt="Location"
        />
        <span>{{ result.destination }}</span>
      </div>
      <template v-if="searchLoading">
        <div class="mx-3 my-2 d-flex" v-for="i in 10" :key="i">
          <b-skeleton height="20px" width="20px" class="mr-2"></b-skeleton>
          <b-skeleton
            height="20px"
            :width="`${Math.floor(Math.random() * (80 - 30 + 1)) + 30}%`"
          ></b-skeleton>
        </div>
      </template>
      <div
        class="my-3 text-center"
        v-if="!filteredResults.length && !searchLoading"
      >
        {{ $t("listing.noResult") }}
      </div>
      <InfiniteLoading
        ref="infiniteLoading"
        @infinite="infiniteHandler"
        :identifier="infiniteId"
      >
        <div slot="spinner"></div>
        <div slot="no-results"></div>
      </InfiniteLoading>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters } from "vuex";
import { currentConfigKey } from "../../helpers/theme";
import InfiniteLoading from "vue-infinite-loading";
import { SearchClient as TypesenseSearchClient } from "typesense";

const COLLECTION = "gloveler_properties";
const PER_PAGE = 15;

export default {
  name: "TypesenseSearch",
  data() {
    return {
      page: 1,
      mode: "city",
      listId: null,
      searchFilter: "",
      showWidget: false,
      searchLoading: false,
      filteredResults: [],
      typesenseClient: null,
      filteredResultsMap: {},
      googlePlacesMap: {},
      infiniteId: +new Date(),
      defaultTypesenseResult: null,
    };
  },
  components: {
    InfiniteLoading,
  },
  props: {
    top: {
      type: String,
      default: "90px",
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  async created() {
    this.listId = this.bookingEngineId;
    this.typesenseClient = new TypesenseSearchClient({
      nodes: [
        {
          host: process.env.VUE_APP_TYPESENSE_HOST,
          port: "",
          protocol: process.env.VUE_APP_TYPESENSE_PROTOCOL,
        },
      ],
      apiKey: process.env.VUE_APP_TYPESENSE_API_KEY,
      connectionTimeoutSeconds: 10,
    });
  },
  async mounted() {
    document.addEventListener("click", this.handleClickOutside);

    await this.$nextTick();
    this.searchLoading = true;
    try {
      this.defaultTypesenseResult = await this.fetchDefaultResults();
    } catch (error) {
      console.error("Error loading Typesense results:", error);
      this.defaultTypesenseResult = null;
    }
    this.searchLoading = false;
  },
  computed: {
    ...mapGetters("config", ["bookingEngineId"]),
    mergeTypesenseSearchResult() {
      return !!currentConfigKey("mergeTypesenseSearchResult", false);
    },
    searchPlaceholder() {
      if (this.mergeTypesenseSearchResult) {
        return this.$t("listing.searchLocationOrPropertyName");
      }
      return this.mode === "city"
        ? this.$t("listing.searchByCityNameInput")
        : this.$t("listing.searchByPropertyNameInput");
    },
    baseFilterBy() {
      return [
        "property_status:=ACTIVE",
        "property_isDeleted:=false",
        "property_forTesting:=false",
        "property_latitude:!=0",
        "property_longitude:!=0",
        "has_geo_code:=true",
        `list_ids:=[${this.listId}]`,
      ].join(" && ");
    },
  },
  methods: {
    handleClickOutside(event) {
      if (!this.$el.contains(event.target) && this.showWidget) {
        this.hide();
      }
    },
    show() {
      setTimeout(() => {
        this.showWidget = true;
      }, 100);
    },
    hide() {
      this.showWidget = false;
    },
    switchMode(mode) {
      this.mode = mode;
      this.resetList();
    },
    selectDestination(result) {
      const isProperty =
        result.type === "property" ||
        (!this.mergeTypesenseSearchResult && this.mode === "property");
      this.$emit("select", {
        propertyId: isProperty ? result.property : null,
        destination: result.destination,
        latitude: result.latitude,
        longitude: result.longitude,
      });
      this.hide();
    },
    resetList() {
      this.page = 1;
      this.filteredResults = [];
      this.filteredResultsMap = {};
      this.googlePlacesMap = {};
      this.defaultTypesenseResult = null;
      this.infiniteId += 1;
    },
    citySearchParams(filter = "", page = this.page) {
      return {
        collection: COLLECTION,
        q: filter || "*",
        query_by: "property_cityName",
        group_by: "property_cityName,property_countryName",
        filter_by: this.baseFilterBy,
        page,
        per_page: PER_PAGE,
        sort_by: "property_cityName:asc",
      };
    },
    propertySearchParams(filter = "", page = this.page) {
      return {
        collection: COLLECTION,
        q: filter || "*",
        query_by: "property_name,property_cityName",
        filter_by: this.baseFilterBy,
        page,
        per_page: PER_PAGE,
        sort_by: "property_name:asc",
      };
    },
    typesenseResults(filter = "") {
      let query_by = "property_name,property_cityName";
      let group_by = "";
      if (this.mode === "city") {
        query_by = "property_cityName";
        group_by = "property_cityName,property_countryName";
      }

      const params = {
        q: filter || "*",
        query_by,
        group_by,
        filter_by: this.baseFilterBy,
        page: this.page,
        per_page: PER_PAGE,
        sort_by:
          this.mode === "city" ? "property_cityName:asc" : "property_name:asc",
      };
      return this.typesenseClient
        .collections(COLLECTION)
        .documents()
        .search(params);
    },
    async multiSearchResults(filter = "") {
      const response = await this.typesenseClient.multiSearch.perform({
        searches: [
          this.citySearchParams(filter),
          this.propertySearchParams(filter),
        ],
      });
      return {
        city: response.results?.[0] || { grouped_hits: [], found: 0, page: this.page },
        property: response.results?.[1] || { hits: [], found: 0, page: this.page },
      };
    },
    fetchDefaultResults() {
      if (this.mergeTypesenseSearchResult) {
        return this.multiSearchResults();
      }
      return this.typesenseResults();
    },
    async infiniteHandler($state) {
      const filter = this.searchFilter.trim().toLowerCase();
      this.searchLoading = true;

      try {
        if (this.mergeTypesenseSearchResult) {
          let response;
          if (!filter && this.page === 1 && this.defaultTypesenseResult) {
            this.filteredResults = [];
            response = this.defaultTypesenseResult;
          } else {
            response = await this.multiSearchResults(filter);
          }
          this.populateMergedHits(response, $state);
        } else {
          let response;
          if (!filter && this.page === 1 && this.defaultTypesenseResult) {
            this.filteredResults = [];
            response = this.defaultTypesenseResult;
          } else {
            response = await this.typesenseResults(filter);
          }
          this.populateHits(response, $state);
        }
      } catch (error) {
        console.error("Error loading Typesense results:", error);
        if ($state) {
          $state.complete();
        }
      }

      this.searchLoading = false;
    },
    capitalize(str) {
      return str
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },
    appendCityHits(groupedHits = []) {
      groupedHits.forEach((record) => {
        const city = this.capitalize(record.group_key[0].toLowerCase());
        if (!city) {
          return;
        }
        const country = record.group_key[1].toUpperCase();
        const destination = `${city}${country ? "," : ""} ${country}`.trim();
        const key = `city-${destination
          .split(" ")
          .join("-")
          .toLowerCase()
          .replace(",", "")}`;
        if (key in this.filteredResultsMap) {
          return;
        }
        const hit = record.hits.find(({ document }) => {
          const { property_latitude, property_longitude } = document;
          return (
            property_latitude &&
            property_longitude &&
            parseFloat(property_latitude) !== 0 &&
            parseFloat(property_longitude) !== 0
          );
        });
        if (!hit) {
          return;
        }
        this.filteredResults.push({
          id: key,
          type: "city",
          destination,
          property: hit.document.property_id,
          longitude: hit.document.property_longitude,
          latitude: hit.document.property_latitude,
        });
        this.filteredResultsMap[key] = true;
      });
    },
    appendPropertyHits(hits = []) {
      hits.forEach((property) => {
        const {
          id,
          property_id,
          property_name,
          google_placeID,
          property_cityName,
          property_longitude,
          property_latitude,
          property_DistributorType,
        } = property.document;
        if (!property_name) {
          return;
        }
        const item = {
          id: `property-${id}`,
          type: "property",
          destination: `${property_name}${
            property_cityName ? "," : ""
          } ${property_cityName}`.trim(),
          property: property_id,
          longitude: property_longitude,
          latitude: property_latitude,
        };
        if (item.id in this.filteredResultsMap) {
          return;
        }
        if (google_placeID && google_placeID in this.googlePlacesMap) {
          const { index, type } = this.googlePlacesMap[google_placeID];
          const priorities = currentConfigKey("distributorOrder", {});
          const newPriority = priorities[property_DistributorType] ?? Infinity;
          const currentPriority = priorities[type] ?? Infinity;
          if (newPriority < currentPriority) {
            const removed = this.filteredResults.splice(index, 1)[0];
            if (removed?.id) {
              delete this.filteredResultsMap[removed.id];
            }
          } else {
            return;
          }
        }
        if (
          property_latitude &&
          property_longitude &&
          parseFloat(property_latitude) !== 0 &&
          parseFloat(property_longitude) !== 0
        ) {
          const index = this.filteredResults.push(item) - 1;
          this.filteredResultsMap[item.id] = true;
          if (google_placeID) {
            this.googlePlacesMap[google_placeID] = {
              index,
              type: property_DistributorType,
            };
          }
        }
      });
    },
    populateMergedHits({ city, property }, state = null) {
      this.appendCityHits(city?.grouped_hits || []);
      this.appendPropertyHits(property?.hits || []);

      const cityPages = Math.ceil((city?.found || 0) / PER_PAGE);
      const propertyPages = Math.ceil((property?.found || 0) / PER_PAGE);
      const totalPages = Math.max(cityPages, propertyPages, 1);
      const currentPage = Math.max(city?.page || this.page, property?.page || this.page);

      if (currentPage < totalPages) {
        this.page += 1;
        if (state) {
          state.loaded();
        }
      } else if (state) {
        state.complete();
      }
    },
    populateHits(response, state = null) {
      if (this.mode === "city") {
        (response.grouped_hits || []).forEach((record) => {
          const city = this.capitalize(record.group_key[0].toLowerCase());
          if (city) {
            const country = record.group_key[1].toUpperCase();
            const destination = `${city}${
              country ? "," : ""
            } ${country}`.trim();
            const key = destination
              .split(" ")
              .join("-")
              .toLowerCase()
              .replace(",", "");
            if (!(key in this.filteredResultsMap)) {
              const hit = record.hits.find(({ document }) => {
                const { property_latitude, property_longitude } = document;
                return (
                  property_latitude &&
                  property_longitude &&
                  parseFloat(property_latitude) !== 0 &&
                  parseFloat(property_longitude) !== 0
                );
              });
              if (hit) {
                this.filteredResults.push({
                  id: key,
                  type: "city",
                  destination,
                  property: hit.document.property_id,
                  longitude: hit.document.property_longitude,
                  latitude: hit.document.property_latitude,
                });
                this.filteredResultsMap[key] = true;
              }
            }
          }
        });
      } else {
        (response.hits || []).forEach((property) => {
          const {
            id,
            property_id,
            property_name,
            google_placeID,
            property_cityName,
            property_longitude,
            property_latitude,
            property_DistributorType,
          } = property.document;
          if (property_name) {
            const item = {
              id,
              type: "property",
              destination: `${property_name}${
                property_cityName ? "," : ""
              } ${property_cityName}`.trim(),
              property: property_id,
              longitude: property_longitude,
              latitude: property_latitude,
            };
            if (!(id in this.filteredResultsMap)) {
              if (google_placeID && google_placeID in this.googlePlacesMap) {
                const { index, type } = this.googlePlacesMap[google_placeID];
                const priorities = currentConfigKey("distributorOrder", {});
                const newPriority =
                  priorities[property_DistributorType] ?? Infinity;
                const currentPriority = priorities[type] ?? Infinity;
                if (newPriority < currentPriority) {
                  this.filteredResults.splice(index, 1);
                  delete this.filteredResultsMap[
                    this.filteredResults[index]?.id
                  ];
                } else {
                  return;
                }
              }
              if (
                property_latitude &&
                property_longitude &&
                parseFloat(property_latitude) !== 0 &&
                parseFloat(property_longitude) !== 0
              ) {
                const index = this.filteredResults.push(item) - 1;
                this.filteredResultsMap[id] = true;
                this.googlePlacesMap[google_placeID] = {
                  index,
                  type: property_DistributorType,
                };
              }
            }
          }
        });
      }

      const totalPages = Math.ceil(response.found / PER_PAGE);
      if (response.page < totalPages) {
        this.page += 1;
        if (state) {
          state.loaded();
        }
      } else if (state) {
        state.complete();
      }
    },
    handleSearch: debounce(function search() {
      this.resetList();
    }, 300),
  },
  watch: {
    defaultTypesenseResult: {
      deep: true,
      handler(response) {
        if (!response) {
          return;
        }
        if (this.mergeTypesenseSearchResult) {
          this.populateMergedHits(response);
        } else {
          this.populateHits(response);
        }
      },
    },
    searchFilter(value, oldValue) {
      if (value !== oldValue) {
        this.handleSearch();
      }
    },
    showWidget(value) {
      if (value) {
        setTimeout(() => {
          this.$refs.searchField.focus();
        }, 100);
      }
    },
  },
};
</script>

<style>
.typesense-search-widget {
  position: absolute;
  width: 100%;
  background: white;
  color: #444;
  z-index: 9999;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  font-size: 14px !important;
}

.option-list {
  max-height: 350px;
  overflow-y: auto;
}

.option-item {
  cursor: pointer;
}

.option-item img {
  width: 20px;
}

.option-item:hover {
  background: #f5ffff;
}

input.typesense-search-form {
  min-width: 100% !important;
  background: #fff !important;
  border: 1px solid #ddd !important;
  border-radius: 5px !important;
  height: 50px !important;
  font-size: 14px !important;
  color: #000 !important;
  padding: 10px;
}
input.typesense-search-form::placeholder {
  color: #888;
}

@media screen and (max-width: 600px) {
  input.typesense-search-form {
    text-align: left !important;
    padding-left: 10px !important;
    /* 16px minimum prevents iOS Safari from zooming on focus */
    font-size: 16px !important;
  }
}
</style>
